import { useState, useEffect } from "react";
import { createCustomer, getConfig, ApiError } from "../services/gridApi";
import type { CustomerType } from "../types";

interface CreateCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (customer: any) => void;
}

function generateId(): string {
  return "cust-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 7);
}

const SAMPLE_INDIVIDUALS = [
  { fullName: "Alice Johnson", email: "alice.johnson@example.com", phoneNumber: "+14155551234", birthDate: "1990-03-15", nationality: "US", line1: "123 Main St", city: "San Francisco", state: "CA", postalCode: "94102", country: "US" },
  { fullName: "Carlos Rivera", email: "carlos.rivera@example.com", phoneNumber: "+525551234567", birthDate: "1985-07-22", nationality: "MX", line1: "Av. Reforma 500", city: "Mexico City", state: "CDMX", postalCode: "06600", country: "MX" },
  { fullName: "Maria Silva", email: "maria.silva@example.com", phoneNumber: "+5511999887766", birthDate: "1992-11-08", nationality: "BR", line1: "Rua Augusta 1200", city: "Sao Paulo", state: "SP", postalCode: "01304-001", country: "BR" },
  { fullName: "James Williams", email: "james.w@example.com", phoneNumber: "+447911123456", birthDate: "1988-01-30", nationality: "GB", line1: "10 Downing Lane", city: "London", state: "", postalCode: "SW1A 2AA", country: "GB" },
  { fullName: "Yuki Tanaka", email: "yuki.tanaka@example.com", phoneNumber: "+81312345678", birthDate: "1995-06-12", nationality: "JP", line1: "1-1 Shibuya", city: "Tokyo", state: "", postalCode: "150-0002", country: "JP" },
];

const SAMPLE_BUSINESSES = [
  { legalName: "Acme Payments Inc.", registrationNumber: "12345678", taxId: "87-6543210", line1: "456 Market St", city: "San Francisco", state: "CA", postalCode: "94105", country: "US" },
  { legalName: "Global Trade Ltd.", registrationNumber: "GB9876543", taxId: "GB-999999", line1: "1 Canary Wharf", city: "London", state: "", postalCode: "E14 5AB", country: "GB" },
  { legalName: "FinTech Brasil LTDA", registrationNumber: "BR1122334", taxId: "12.345.678/0001-90", line1: "Av Paulista 900", city: "Sao Paulo", state: "SP", postalCode: "01310-100", country: "BR" },
];

export default function CreateCustomerModal({
  isOpen,
  onClose,
  onCreated,
}: CreateCustomerModalProps) {
  const [customerType, setCustomerType] = useState<CustomerType>("INDIVIDUAL");
  const [platformConfig, setPlatformConfig] = useState<any>(null);

  // Individual fields
  const [fullName, setFullName] = useState(SAMPLE_INDIVIDUALS[0].fullName);
  const [email, setEmail] = useState(SAMPLE_INDIVIDUALS[0].email);
  const [phoneNumber, setPhoneNumber] = useState(SAMPLE_INDIVIDUALS[0].phoneNumber);
  const [birthDate, setBirthDate] = useState(SAMPLE_INDIVIDUALS[0].birthDate);
  const [nationality, setNationality] = useState(SAMPLE_INDIVIDUALS[0].nationality);

  // Business fields
  const [legalName, setLegalName] = useState(SAMPLE_BUSINESSES[0].legalName);
  const [registrationNumber, setRegistrationNumber] = useState(SAMPLE_BUSINESSES[0].registrationNumber);
  const [taxId, setTaxId] = useState(SAMPLE_BUSINESSES[0].taxId);

  // Address
  const [line1, setLine1] = useState(SAMPLE_INDIVIDUALS[0].line1);
  const [city, setCity] = useState(SAMPLE_INDIVIDUALS[0].city);
  const [state, setState] = useState(SAMPLE_INDIVIDUALS[0].state);
  const [postalCode, setPostalCode] = useState(SAMPLE_INDIVIDUALS[0].postalCode);
  const [country, setCountry] = useState(SAMPLE_INDIVIDUALS[0].country);

  // API state
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Fetch platform config on mount
  useEffect(() => {
    if (isOpen && !platformConfig) {
      getConfig().then(setPlatformConfig).catch(() => {});
    }
  }, [isOpen, platformConfig]);

  const supportedCurrencies = platformConfig?.supportedCurrencies || [];
  const requiredFields = new Set<string>();
  for (const curr of supportedCurrencies) {
    for (const f of curr.providerRequiredCustomerFields || []) {
      requiredFields.add(f);
    }
  }

  const randomizeIndividual = () => {
    const s = SAMPLE_INDIVIDUALS[Math.floor(Math.random() * SAMPLE_INDIVIDUALS.length)];
    setFullName(s.fullName);
    setEmail(s.email);
    setPhoneNumber(s.phoneNumber);
    setBirthDate(s.birthDate);
    setNationality(s.nationality);
    setLine1(s.line1);
    setCity(s.city);
    setState(s.state);
    setPostalCode(s.postalCode);
    setCountry(s.country);
  };

  const randomizeBusiness = () => {
    const s = SAMPLE_BUSINESSES[Math.floor(Math.random() * SAMPLE_BUSINESSES.length)];
    setLegalName(s.legalName);
    setRegistrationNumber(s.registrationNumber);
    setTaxId(s.taxId);
    setLine1(s.line1);
    setCity(s.city);
    setState(s.state);
    setPostalCode(s.postalCode);
    setCountry(s.country);
  };

  const reset = () => {
    setCustomerType("INDIVIDUAL");
    setFullName(SAMPLE_INDIVIDUALS[0].fullName);
    setEmail(SAMPLE_INDIVIDUALS[0].email);
    setPhoneNumber(SAMPLE_INDIVIDUALS[0].phoneNumber);
    setBirthDate(SAMPLE_INDIVIDUALS[0].birthDate);
    setNationality(SAMPLE_INDIVIDUALS[0].nationality);
    setLegalName(SAMPLE_BUSINESSES[0].legalName);
    setRegistrationNumber(SAMPLE_BUSINESSES[0].registrationNumber);
    setTaxId(SAMPLE_BUSINESSES[0].taxId);
    setLine1(SAMPLE_INDIVIDUALS[0].line1);
    setCity(SAMPLE_INDIVIDUALS[0].city);
    setState(SAMPLE_INDIVIDUALS[0].state);
    setPostalCode(SAMPLE_INDIVIDUALS[0].postalCode);
    setCountry(SAMPLE_INDIVIDUALS[0].country);
    setApiError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setApiError(null);

    const platformCustomerId = generateId();

    try {
      const body: Record<string, unknown> = {
        customerType,
        platformCustomerId,
      };

      // Always include address
      if (line1.trim()) {
        const addr: Record<string, string> = {
          line1: line1.trim(),
          city: city.trim(),
          postalCode: postalCode.trim(),
          country: country.trim(),
        };
        if (state.trim()) addr.state = state.trim();
        body.address = addr;
      }

      if (customerType === "INDIVIDUAL") {
        if (fullName.trim()) body.fullName = fullName.trim();
        if (birthDate.trim()) body.birthDate = birthDate.trim();
        if (nationality.trim()) body.nationality = nationality.trim();
        if (email.trim()) body.email = email.trim();
        if (phoneNumber.trim()) body.phoneNumber = phoneNumber.trim();
      }

      if (customerType === "BUSINESS") {
        const biz: Record<string, string> = {
          legalName: legalName.trim(),
        };
        if (registrationNumber.trim()) biz.registrationNumber = registrationNumber.trim();
        if (taxId.trim()) biz.taxId = taxId.trim();
        body.businessInfo = biz;
      }

      console.log("[CreateCustomer] Sending:", JSON.stringify(body, null, 2));
      const created = await createCustomer(body);
      console.log("[CreateCustomer] Response:", JSON.stringify(created, null, 2));
      reset();
      onClose();
      onCreated(created);
    } catch (err) {
      if (err instanceof ApiError) {
        setApiError(`${err.message} (${err.status})`);
        console.error("[CreateCustomer] Error body:", err.body);
      } else {
        setApiError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = customerType === "INDIVIDUAL" ? fullName.trim() : legalName.trim();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[#111111] border border-[#222222] rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-[#222222] sticky top-0 bg-[#111111] z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#F8F8F7]">Create Customer</h2>
              <p className="text-xs text-[#00B3E0] font-mono mt-0.5">POST /customers</p>
            </div>
            <button
              onClick={() => { reset(); onClose(); }}
              className="text-[#8A8A8A] hover:text-[#F8F8F7]"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {apiError && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 rounded-lg text-sm">
              {apiError}
            </div>
          )}

          {/* Platform Config Info */}
          {supportedCurrencies.length > 0 && (
            <div className="bg-[#0A1628] border border-[#1A3A5C] rounded-lg p-3">
              <div className="text-xs text-[#00B3E0] font-medium mb-1">Platform Config</div>
              <div className="text-xs text-[#8A8A8A]">
                Currencies: <span className="text-[#CCCCCC] font-mono">{supportedCurrencies.map((c: any) => c.currencyCode).join(", ")}</span>
              </div>
              {requiredFields.size > 0 && (
                <div className="text-xs text-[#8A8A8A] mt-1">
                  Required fields: <span className="text-yellow-400/80 font-mono">{Array.from(requiredFields).join(", ")}</span>
                </div>
              )}
              <div className="text-[11px] text-[#555555] mt-1">
                Internal accounts will be auto-created for each currency above.
              </div>
            </div>
          )}

          {/* Customer Type */}
          <div>
            <label className="block text-xs text-[#8A8A8A] mb-2">Customer Type</label>
            <div className="flex gap-2">
              {(["INDIVIDUAL", "BUSINESS"] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setCustomerType(type)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                    customerType === type
                      ? "bg-[#00B3E0]/10 text-[#00B3E0] border border-[#00B3E0]/20"
                      : "bg-[#0A0A0A] text-[#8A8A8A] border border-[#333333] hover:border-[#555555]"
                  }`}
                >
                  {type === "INDIVIDUAL" ? "Individual" : "Business"}
                </button>
              ))}
            </div>
          </div>

          {/* Auto-generated ID notice */}
          <div className="bg-[#0A1628] border border-[#1A3A5C] rounded-lg p-3">
            <div className="text-xs text-[#00B3E0] font-medium">Platform Customer ID</div>
            <div className="text-xs text-[#8A8A8A] mt-0.5">
              Auto-generated on submit (e.g. <span className="font-mono text-[#CCCCCC]">{generateId()}</span>)
            </div>
          </div>

          {/* Randomize button */}
          <button
            type="button"
            onClick={customerType === "INDIVIDUAL" ? randomizeIndividual : randomizeBusiness}
            className="w-full border border-[#333333] text-[#CCCCCC] py-2 rounded-lg hover:bg-[#222222] text-sm"
          >
            Randomize Sample Data
          </button>

          {/* Individual Fields */}
          {customerType === "INDIVIDUAL" && (
            <div className="space-y-3">
              <div className="text-xs uppercase tracking-wider text-[#8A8A8A] font-medium">Personal Info</div>
              <div>
                <label className="block text-xs text-[#8A8A8A] mb-1">
                  Full Name {requiredFields.has("FULL_NAME") && <span className="text-yellow-400">*</span>}
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#8A8A8A] mb-1">
                    Email {requiredFields.has("EMAIL") && <span className="text-yellow-400">*</span>}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#8A8A8A] mb-1">
                    Phone {requiredFields.has("PHONE_NUMBER") && <span className="text-yellow-400">*</span>}
                  </label>
                  <input
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#8A8A8A] mb-1">
                    Birth Date {requiredFields.has("BIRTH_DATE") && <span className="text-yellow-400">*</span>}
                  </label>
                  <input
                    type="text"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    placeholder="YYYY-MM-DD"
                    className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#8A8A8A] mb-1">
                    Nationality {requiredFields.has("NATIONALITY") && <span className="text-yellow-400">*</span>}
                  </label>
                  <input
                    type="text"
                    value={nationality}
                    onChange={(e) => setNationality(e.target.value)}
                    maxLength={2}
                    className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Business Fields */}
          {customerType === "BUSINESS" && (
            <div className="space-y-3">
              <div className="text-xs uppercase tracking-wider text-[#8A8A8A] font-medium">Business Info</div>
              <div>
                <label className="block text-xs text-[#8A8A8A] mb-1">Legal Name</label>
                <input
                  type="text"
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#8A8A8A] mb-1">Registration #</label>
                  <input
                    type="text"
                    value={registrationNumber}
                    onChange={(e) => setRegistrationNumber(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#8A8A8A] mb-1">Tax ID</label>
                  <input
                    type="text"
                    value={taxId}
                    onChange={(e) => setTaxId(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Address */}
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wider text-[#8A8A8A] font-medium">
              Address {requiredFields.has("POSTAL_ADDRESS") && <span className="text-yellow-400">*</span>}
            </div>
            <div>
              <label className="block text-xs text-[#8A8A8A] mb-1">Street</label>
              <input
                type="text"
                value={line1}
                onChange={(e) => setLine1(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[#8A8A8A] mb-1">City</label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-[#8A8A8A] mb-1">State</label>
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[#8A8A8A] mb-1">Postal Code</label>
                <input
                  type="text"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-[#8A8A8A] mb-1">Country</label>
                <input
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  maxLength={2}
                  className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => { reset(); onClose(); }}
              disabled={submitting}
              className="flex-1 border border-[#333333] text-[#F8F8F7] py-2.5 rounded-lg hover:bg-[#222222]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid || submitting}
              className="flex-1 bg-[#00B3E0] text-black font-semibold py-2.5 rounded-lg hover:bg-[#00C4F5] disabled:opacity-50"
            >
              {submitting ? "Creating..." : "Create Customer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
